/* ============================================================
   SINGSPACE ADMIN - SERVICE API
   ============================================================ */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 - try refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/admin/refresh`, {
            refreshToken,
          });

          const { accessToken } = response.data.data;
          localStorage.setItem('accessToken', accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  },
);

// API helper functions
export const apiService = {
  // Auth
  login: (email: string, password: string) => api.post('/auth/admin/login', { email, password }),

  // Spaces
  getSpaces: (params?: Record<string, unknown>) => api.get('/spaces/admin/all', { params }),
  getSpace: (id: string) => api.get(`/spaces/admin/${id}`),
  createSpace: (data: unknown) => api.post('/spaces/admin', data),
  /** @deprecated Utiliser les endpoints spécialisés */
  updateSpace: (id: string, data: unknown) => api.put(`/spaces/admin/${id}`, data),
  deleteSpace: (id: string) => api.delete(`/spaces/admin/${id}`),
  toggleSpace: (id: string, field: string) => api.patch(`/spaces/admin/${id}/toggle`, { field }),
  duplicateSpace: (id: string) => api.post(`/spaces/admin/${id}/duplicate`),

  // Spaces — Endpoints spécialisés de mise à jour
  updateSpaceScalars: (id: string, data: Record<string, unknown>) => api.put(`/spaces/admin/${id}/scalars`, data),
  updateSpaceFormSections: (id: string, formSections: unknown[]) =>
    api.put(`/spaces/admin/${id}/form-sections`, { formSections }),
  updateSpaceEventTypes: (id: string, eventTypes: unknown[]) =>
    api.put(`/spaces/admin/${id}/event-types`, { eventTypes }),
  updateSpaceDisplay: (id: string, data: Record<string, unknown>) => api.put(`/spaces/admin/${id}/display`, data),
  /** Sauvegarde finale du wizard — payload structuré par domaine */
  commitSpace: (id: string, data: Record<string, unknown>) => api.put(`/spaces/admin/${id}/commit`, data),

  // Pricing
  getPricing: (spaceId: string) => api.get(`/pricing/admin/${spaceId}`),
  updatePricing: (spaceId: string, pricingGroups: unknown) => api.put(`/pricing/admin/${spaceId}`, { pricingGroups }),

  // Requester Config
  updateRequesterConfig: (spaceId: string, config: unknown) =>
    api.patch(`/spaces/admin/${spaceId}/requester-config`, config),

  // Required Documents
  getRequiredDocuments: (spaceId: string) => api.get(`/spaces/admin/${spaceId}/required-documents`),
  updateRequiredDocuments: (spaceId: string, documents: unknown) =>
    api.put(`/spaces/admin/${spaceId}/required-documents`, { documents }),

  // Capacity Brackets
  getCapacityBrackets: (spaceId: string) => api.get(`/spaces/admin/${spaceId}/capacity-brackets`),
  updateCapacityBrackets: (spaceId: string, brackets: unknown) =>
    api.put(`/spaces/admin/${spaceId}/capacity-brackets`, { brackets }),

  // Room Layouts (mises en place)
  getRoomLayouts: (spaceId: string) => api.get(`/spaces/admin/${spaceId}/room-layouts`),
  updateRoomLayouts: (spaceId: string, layouts: unknown) =>
    api.put(`/spaces/admin/${spaceId}/room-layouts`, { layouts }),

  // Equipments
  getEquipments: (spaceId: string) => api.get(`/spaces/admin/${spaceId}/equipments`),
  updateEquipments: (spaceId: string, equipments: unknown) =>
    api.put(`/spaces/admin/${spaceId}/equipments`, { equipments }),

  // Catering Prices
  getCateringPrices: (spaceId: string) => api.get(`/spaces/admin/${spaceId}/catering-prices`),
  updateCateringPrices: (spaceId: string, cateringPrices: unknown) =>
    api.put(`/spaces/admin/${spaceId}/catering-prices`, { cateringPrices }),

  // Bookings
  getBookings: (params?: Record<string, unknown>) => api.get('/bookings/admin/all', { params }),
  getBooking: (id: string) => api.get(`/bookings/admin/${id}`),
  updateBookingStatus: (id: string, status: string, notes?: string) =>
    api.patch(`/bookings/admin/${id}/status`, { status, adminNotes: notes }),
  confirmPayment: (id: string) => api.post(`/bookings/admin/${id}/confirm-payment`),
  deleteBooking: (id: string) => api.delete(`/bookings/admin/${id}`),

  // Users
  getUsers: () => api.get('/users/admin'),
  createUser: (data: unknown) => api.post('/users/admin', data),
  updateUser: (id: string, data: unknown) => api.put(`/users/admin/${id}`, data),
  deleteUser: (id: string) => api.delete(`/users/admin/${id}`),

  // Clients
  getClients: (params?: Record<string, unknown>) => api.get('/users/clients', { params }),
  getClient: (id: string) => api.get(`/users/clients/${id}`),

  // Calendar
  getCalendarEvents: (params?: Record<string, string>) => api.get('/calendar/events', { params }),

  // Google Auth / Cloud Integration
  getGoogleAuthStatus: () => api.get('/auth/google/status'),
  getGoogleAuthUrl: () => api.get('/auth/google/url'),
  disconnectGoogleAuth: () => api.post('/auth/google/disconnect'),
};
